import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchProducts, 
  selectProductsState, 
  resetProductsState,
  addToCart,
  fetchCartItems,
  selectIsUserLoggedIn
} from '../../../redux/WithcookieSlice';
import { FilterOptions, Product } from './ProductList.types';
import { adaptProductListProps } from './ProductList.adapter';
import { PriceDisplay, DiscountRatePriceDisplay } from '../../common/PriceDisplay';
import SafeImage from '../../common/SafeImage';
import { useTranslation } from '../../../redux/TranslationHelper';
import './ProductList.css';
import { AnyAction } from '@reduxjs/toolkit';
import { Link, useNavigate } from 'react-router-dom';

// Window 객체 타입 확장
declare global {
  interface Window {
    withcookieBackState?: {
      isRestoring: boolean;
    };
  }
}

/**
 * CSS Position 타입 정의
 */
type CSSPosition = 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';

/**
 * 확장된 CSS 스타일 타입
 */
interface ExtendedCSSProperties extends React.CSSProperties {
  position?: CSSPosition;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

/**
 * 에러 인터페이스 정의
 */
interface ErrorWithMessage {
  message: string;
  [key: string]: any;
}

/**
 * 에러가 message 프로퍼티를 갖고 있는지 확인하는 타입 가드
 */
function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

/**
 * 상품 항목당 XL 값 계산 함수
 */
function calculateXlValue(items: number | string): number {
  const itemCount = Number(items);
  
  switch(itemCount) {
    case 2: return 6;
    case 3: return 4;
    case 6: return 2;
    default: return 3; // 기본값 4개 (xl=3)
  }
}

/**
 * 상품 목록 컴포넌트
 */
const ProductListComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환 - useMemo로 메모이제이션
  const adaptedProps = useMemo(() => adaptProductListProps(props), [props]);
  
  // 다국어 지원 훅 사용
  const { t } = useTranslation();
  
  // props 구조 분해 할당
  const { 
    id,
    componentProps = {}
  } = adaptedProps;
  
  // componentProps에서 필요한 값 추출 - useMemo로 메모이제이션
  const { 
    order = "", 
    lg = 12, 
    className = "padding-tb-40",
    itemsPerRow = 4,
    categoryId = '',
    excludeIndexes = [], 
    showStock = false,
    include_product_ids = [], 
    exclude_product_ids = [], 
    include_category_ids = [], 
    exclude_category_ids = [], 
    itemsPerPage = 20
  } = useMemo(() => componentProps, [componentProps]);
  
  // Redux 관련 설정
  const dispatch = useDispatch();
  const { products, loading, error, totalCount, totalPages: reduxTotalPages } = useSelector(selectProductsState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  
  // 상태 관리
  const [isGridView] = useState(false);
  const [xlValue, setXlValue] = useState(() => calculateXlValue(itemsPerRow)); 
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileProducts, setMobileProducts] = useState<Product[]>([]);
  const [mobilePage, setMobilePage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // 레퍼런스 관리
  const initialLoadCompletedRef = useRef(false);
  const prevFiltersRef = useRef<string | null>(null);
  const loadMoreButtonRef = useRef<HTMLButtonElement | null>(null);
  const autoLoadTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  
  // 필터 옵션 메모이제이션
  const filterOptions = useMemo(() => {
    return {
      categoryId,
      include_product_ids,
      exclude_product_ids,
      include_category_ids,
      exclude_category_ids,
      currentPage,
      itemsPerPage
    };
  }, [
    categoryId, 
    include_product_ids, 
    exclude_product_ids, 
    include_category_ids, 
    exclude_category_ids, 
    currentPage, 
    itemsPerPage
  ]);
  
  // 컴포넌트 CSS 스타일
  const productImageStyle = useMemo(() => ({
    imgContainer: {
      width: '100%',
      position: 'relative' as CSSPosition,
      paddingTop: '100%', // 1:1 비율 유지
      overflow: 'hidden',
      backgroundColor: '#f8f8f8',
      borderRadius: '0', // 둥근 모서리 제거
    } as ExtendedCSSProperties,
    imgWrapper: {
      position: 'absolute' as CSSPosition,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    } as ExtendedCSSProperties,
    imageLink: {
      width: "100%",
      height: "100%",
      display: "block",
      position: "relative" as CSSPosition,
    } as ExtendedCSSProperties,
    mainImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover" as 'cover',
      position: "absolute" as CSSPosition,
      top: 0,
      left: 0,
      transition: "opacity 0.3s ease",
    } as ExtendedCSSProperties,
    hoverImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover" as 'cover',
      position: "absolute" as CSSPosition,
      top: 0,
      left: 0,
      opacity: 0,
      transition: "opacity 0.3s ease",
    } as ExtendedCSSProperties,
    productAction: {
      position: 'absolute' as CSSPosition,
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'center',
      background: 'rgba(167, 73, 255, 0.9)',
      padding: '10px',
      opacity: 0,
      transform: 'translateY(20px)',
      transition: 'all 0.3s ease',
      zIndex: 9
    } as ExtendedCSSProperties,
    actionButton: {
      background: 'transparent',
      border: 'none',
      color: 'white',
      fontSize: '16px',
      padding: '8px 12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    } as ExtendedCSSProperties
  }), []);
  
  // navigate 훅 추가
  const navigate = useNavigate();
  
  // 장바구니 추가 핸들러 - useCallback으로 메모이제이션
  const handleAddToCart = useCallback(async (product: Product) => {
    // hasOptions가 true인 경우 상세페이지로 이동
    if (product.hasOptions) {
      navigate(`/products/${product.id}`);
      return;
    }
    
    try {
      // 로그인 체크 - Redux 상태만 사용
      if (!isUserLoggedIn) {
        alert(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동합니다.'));
        window.location.href = '/login';
        return;
      }
      
      // product.id를 숫자로 변환
      const productId = typeof product.id === 'string' ? parseInt(product.id, 10) : Number(product.id);
      
      // 장바구니에 상품 추가 - AnyAction으로 타입 캐스팅하여 TypeScript 오류 해결
      await dispatch(addToCart({ product_id: productId, count: 1 }) as unknown as AnyAction);
      await dispatch(fetchCartItems() as unknown as AnyAction);
      alert(t('상품') + ` "${product.title}"` + t('이(가) 장바구니에 추가되었습니다.'));
    } catch (error: any) {
      console.error(t('장바구니 추가 중 오류:'), error);
      
      // 오류가 인증 관련 문제인 경우
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동합니다.'));
        window.location.href = '/login';
      } else {
        alert(t('장바구니 추가에 실패했습니다. 다시 시도해주세요.'));
      }
    }
  }, [isUserLoggedIn, t, dispatch, navigate]);
  
  // 이미지 호버 이벤트 핸들러 - useCallback으로 메모이제이션
  const handleImageHover = useCallback((e: React.MouseEvent, isHover: boolean) => {
    // 이미지 요소가 존재하는 경우에만 호버 효과 적용
    const mainImage = e.currentTarget.querySelector('.main-image') as HTMLElement;
    const hoverImage = e.currentTarget.querySelector('.hover-image') as HTMLElement;
    
    // 이미지가 존재하는 경우에만 호버 효과 적용
    if (mainImage && hoverImage) {
      mainImage.style.opacity = isHover ? '0' : '1';
      hoverImage.style.opacity = isHover ? '1' : '0';
    }
  }, []);
  
  // 페이지 변경 함수 - useCallback으로 메모이제이션
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }
    
    // 현재 페이지와 같은 페이지를 클릭한 경우 상단으로 스크롤만 실행
    if (newPage === currentPage) {
      window.scrollTo(0, 0);
      return;
    }
    
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // 페이지 상단으로 스크롤
  }, [totalPages, currentPage]);
  
  // 필터 옵션 객체 생성 함수 - useCallback으로 메모이제이션
  const getFilterOptions = useCallback((): FilterOptions => {
    const options: FilterOptions = {
      page: isRestoringFromBackRef.current ? 1 : currentPage, // 뒤로가기 복원 중일 때는 항상 1페이지
      per_page: itemsPerPage
    };
    
    // 모든 값을 숫자 배열로 변환하는 함수
    const toIntArray = (value: any): number[] => {
      // 값이 없으면 빈 배열 반환
      if (!value) return [];
      
      // 이미 배열인 경우
      if (Array.isArray(value)) {
        // 배열의 각 요소를 정수로 변환
        return value
          .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
          .filter(id => !isNaN(id));
      }
      
      // 문자열이나 단일 값인 경우 정수로 변환하여 배열에 넣음
      const parsed = typeof value === 'string' ? parseInt(value, 10) : Number(value);
      return !isNaN(parsed) ? [parsed] : [];
    };

    // ID 관련 필드는 항상 정수 배열로 변환
    const includeProductIds = toIntArray(include_product_ids);
    const excludeProductIds = toIntArray(exclude_product_ids);
    const includeCategoryIds = toIntArray(include_category_ids);
    const excludeCategoryIds = toIntArray(exclude_category_ids);
    
    // 배열이 비어있지 않은 경우에만 옵션에 추가
    if (includeProductIds.length > 0) {
      options.include_product_ids = includeProductIds;
    }
    
    if (excludeProductIds.length > 0) {
      options.exclude_product_ids = excludeProductIds;
    }
    
    if (includeCategoryIds.length > 0) {
      options.include_category_ids = includeCategoryIds;
    }
    
    if (excludeCategoryIds.length > 0) {
      options.exclude_category_ids = excludeCategoryIds;
    }
    
    // 카테고리 ID가 있으면 추가 (정수로 변환)
    if (categoryId) {
      const categoryIdInt = typeof categoryId === 'string' ? parseInt(categoryId, 10) : Number(categoryId);
      if (!isNaN(categoryIdInt)) {
        options.category_id = categoryIdInt;
      }
    }
    
    return options;
  }, [currentPage, itemsPerPage, include_product_ids, exclude_product_ids, include_category_ids, exclude_category_ids, categoryId]);
  
  
  // 한 줄에 표시할 항목 수에 따른 클래스 계산 - useCallback으로 메모이제이션
  const getItemsPerRowClass = useCallback(() => {
    return `items-per-row-${itemsPerRow}`;
  }, [itemsPerRow]);
  
  // 페이지네이션 렌더링 함수 - useCallback으로 메모이제이션
  const renderPagination = useCallback(() => {
    // 표시할 페이지 버튼 수 계산
    const pageButtonsToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(pageButtonsToShow / 2));
    let endPage = Math.min(totalPages, startPage + pageButtonsToShow - 1);
    
    // 페이지 범위 조정
    if (endPage - startPage + 1 < pageButtonsToShow && totalPages > pageButtonsToShow) {
      startPage = Math.max(1, endPage - pageButtonsToShow + 1);
    }
    
    // 페이지 버튼 배열 생성
    const pageButtons = [];
    
    // 이전 페이지 버튼
    pageButtons.push(
      <li key="prev" className={currentPage === 1 ? "disabled" : ""}>
        <a href="#!" onClick={(e) => {
          e.preventDefault();
          if (currentPage > 1) handlePageChange(currentPage - 1);
        }}>
          {t('이전')}
        </a>
      </li>
    );
    
    // 첫 페이지 버튼 (시작 페이지가 1이 아닌 경우)
    if (startPage > 1) {
      pageButtons.push(
        <li key="first">
          <a href="#!" onClick={(e) => {
            e.preventDefault();
            handlePageChange(1);
          }}>
            1
          </a>
        </li>
      );
      
      // 생략 표시 (시작 페이지가 2보다 크면)
      if (startPage > 2) {
        pageButtons.push(
          <li key="ellipsis1" className="disabled">
            <a href="#!">...</a>
          </li>
        );
      }
    }
    
    // 페이지 번호 버튼
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li key={i} className={i === currentPage ? "active" : ""}>
          <a href="#!" onClick={(e) => {
            e.preventDefault();
            handlePageChange(i);
          }}>
            {i}
          </a>
        </li>
      );
    }
    
    // 마지막 페이지 버튼 (끝 페이지가 totalPages보다 작은 경우)
    if (endPage < totalPages) {
      // 생략 표시 (끝 페이지가 totalPages-1보다 작으면)
      if (endPage < totalPages - 1) {
        pageButtons.push(
          <li key="ellipsis2" className="disabled">
            <a href="#!">...</a>
          </li>
        );
      }
      
      pageButtons.push(
        <li key="last">
          <a href="#!" onClick={(e) => {
            e.preventDefault();
            handlePageChange(totalPages);
          }}>
            {totalPages}
          </a>
        </li>
      );
    }
    
    // 다음 페이지 버튼
    pageButtons.push(
      <li key="next" className={currentPage === totalPages ? "disabled" : ""}>
        <a href="#!" onClick={(e) => {
          e.preventDefault();
          if (currentPage < totalPages) handlePageChange(currentPage + 1);
        }}>
          {t('다음')}
        </a>
      </li>
    );
    
    return (
      <ul className="pagination" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        {pageButtons}
      </ul>
    );
  }, [currentPage, totalPages, handlePageChange, t]);
  
  // sessionStorage 유틸리티 함수들
  const getStorageKey = useCallback((categoryId: string | number) => {
    return `mobile-products-${String(categoryId) || 'all'}`;
  }, []);

  const saveMobileState = useCallback((data: {
    mobileProducts: Product[];
    mobilePage: number;
    scrollPosition: number;
    totalPages: number;
  }) => {
    try {
      const key = getStorageKey(categoryId);
      sessionStorage.setItem(key, JSON.stringify({
        ...data,
        timestamp: Date.now(),
        categoryId
      }));
    } catch (error) {
      console.warn('Failed to save mobile state:', error);
    }
  }, [categoryId, getStorageKey]);

  const loadMobileState = useCallback(() => {
    try {
      const key = getStorageKey(categoryId);
      const saved = sessionStorage.getItem(key);
      if (saved) {
        const data = JSON.parse(saved);
        // 30분 이내 데이터만 사용
        if (Date.now() - data.timestamp < 30 * 60 * 1000) {
          return data;
        }
      }
    } catch (error) {
      console.warn('Failed to load mobile state:', error);
    }
    return null;
  }, [categoryId, getStorageKey]);

  const clearMobileState = useCallback(() => {
    try {
      const key = getStorageKey(categoryId);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear mobile state:', error);
    }
  }, [categoryId, getStorageKey]);

  // 뒤로가기 감지를 위한 전역 상태 (컴포넌트 언마운트되어도 유지)
  const isRestoringFromBackRef = useRef(false);
  
  // 전역 뒤로가기 플래그 (window 객체에 저장하여 컴포넌트 재마운트 시에도 유지)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!window.withcookieBackState) {
        window.withcookieBackState = { isRestoring: false };
      }
    }
  }, []);
  
  // 스크롤 저장을 위한 throttle ref
  const scrollSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 컴포넌트 언마운트 시 상품 상태 초기화
  useEffect(() => {
    return () => {
      dispatch(resetProductsState());
    };
  }, [dispatch]);
  
  // 페이지네이션 데이터 업데이트
  useEffect(() => {
    // Redux에서 받은 totalPages와 totalCount 사용
    if (reduxTotalPages !== undefined) {
      setTotalPages(reduxTotalPages);
    }
  }, [reduxTotalPages, totalCount]);
  
  // API 호출 중복 방지를 위한 ref
  const fetchProductsInProgressRef = useRef(false);
  
  // 상품 필터링 및 API 호출 - useEffect의 의존성 배열 최적화
  useEffect(() => {

    try {
      // 뒤로가기 복원 상태 확인 (전역 상태 우선, 로컬 ref는 백업)
      const isRestoring = (typeof window !== 'undefined' && window.withcookieBackState?.isRestoring) || isRestoringFromBackRef.current;
      
      // 상품 로드 중이거나 이미 API 호출 진행 중이거나 뒤로가기 복원 중이면 생략
      if (loading || fetchProductsInProgressRef.current || isRestoring) {
        return;
      }
      
      // 현재 필터를 문자열로 변환 (비교용)
      const currentFilters = {
        categoryId: categoryId || "",
        include_product_ids,
        exclude_product_ids,
        include_category_ids,
        exclude_category_ids,
        page: currentPage,
        per_page: itemsPerPage
      };
      
      // 필터 문자열로 변환 (비교용)
      const currentFiltersStr = JSON.stringify(currentFilters);
      const prevFiltersStr = prevFiltersRef.current;
      
      // 초기 로드 완료 상태와 필터 비교
      const isFilterChanged = prevFiltersStr !== currentFiltersStr;
      
      // 이전 필터 저장
      prevFiltersRef.current = currentFiltersStr;
      
      // API 호출 여부 결정
      // 1. 초기 로드가 아직 안됐거나
      // 2. 필터가 변경되었을 때만 API 호출 (products 의존성 제거)
      const needsApiCall = !initialLoadCompletedRef.current || isFilterChanged;
      
      if (needsApiCall) {
        // API 호출 시작 표시
        fetchProductsInProgressRef.current = true;
        
        // 현재 카테고리와 상품 ID에 따라 API 호출 옵션 설정
        const apiOptions = getFilterOptions();
        

        // 상품 로딩 액션 디스패치 - AnyAction으로 타입 캐스팅
        dispatch(fetchProducts(apiOptions) as unknown as AnyAction)
          .finally(() => {
            // API 호출 완료 후 플래그 초기화
            fetchProductsInProgressRef.current = false;
          });
        
        // 초기 로드 완료 표시
        initialLoadCompletedRef.current = true;
      }
    } catch (error) {
      console.error(t('예상치 못한 오류:'), error);
      // 오류 발생 시에도 플래그 초기화
      fetchProductsInProgressRef.current = false;
    }
  }, [
    filterOptions, // filterOptions는 모든 필터 값을 포함하는 메모이제이션된 객체
    loading,
    dispatch,
    getFilterOptions,
    t
  ]);
  
  
  // 윈도우 크기 변경 감지하여 반응형 처리
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 767; 
      const tablet = window.innerWidth > 767 && window.innerWidth <= 991;
      
      setIsMobile(mobile);
      
      if (!mobile && !tablet) {
        // PC 환경에서는 항상 4개로 강제 설정 (xlValue = 3)
        setXlValue(3);
      } else if (tablet) {
        // 태블릿에서는 2개 (xlValue = 6)
        setXlValue(6);
      } else {
        // 모바일에서는 2개씩 표시 (xlValue = 6)
        setXlValue(6);
      }
    };

    // 초기 실행
    handleResize();
    
    // 윈도우 리사이즈 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);
    
    // 클린업 함수
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // itemsPerRow가 변경될 때마다 xlValue 업데이트
  useEffect(() => {
    // PC 환경에서는 항상 4개로 강제 설정
    const mobile = window.innerWidth <= 767; 
    const tablet = window.innerWidth > 767 && window.innerWidth <= 991;
    
    if (!mobile && !tablet) {
      // PC 환경에서는 항상 4개로 강제 설정 (xlValue = 3)
      setXlValue(3);
    } else if (mobile) {
      // 모바일에서는 2개씩 표시
      setXlValue(6);
    } else {
      // 태블릿에서는 원래 로직 유지
      const newXlValue = calculateXlValue(itemsPerRow);
      setXlValue(newXlValue);
    }
  }, [itemsPerRow]);
  
  // 컴포넌트 마운트 시 뒤로가기 감지 (새로운 방식)
  useEffect(() => {
    if (!isMobile) return;
    
    // sessionStorage에서 뒤로가기 플래그 확인
    const isFromBack = sessionStorage.getItem('withcookie_from_back') === 'true';

    if (isFromBack) {
      if (window.withcookieBackState) {
        window.withcookieBackState.isRestoring = true;
      }
      isRestoringFromBackRef.current = true;
      
      // 플래그 즉시 제거 (한 번만 사용)
      sessionStorage.removeItem('withcookie_from_back');
      
      // 저장된 상태 복원
      const savedState = loadMobileState();
      if (savedState && savedState.mobileProducts && savedState.mobileProducts.length > 0) {

        // 상태 복원 순서: 1) 상품 목록 2) 페이지 번호 3) 스크롤 위치
        setMobileProducts(savedState.mobileProducts);
        setMobilePage(1); // 항상 1페이지로 이동
        
        // 즉시 복원 완료 처리 (지연 최소화)
        setTimeout(() => {
          window.scrollTo(0, savedState.scrollPosition);
        }, 50);
        
        // 복원 상태 즉시 해제 (UI 차단 방지)
        setTimeout(() => {
          isRestoringFromBackRef.current = false;
          if (window.withcookieBackState) {
            window.withcookieBackState.isRestoring = false;
          }
        }, 100);
      } else {
        // 저장된 상태가 없으면 즉시 복원 완료
        isRestoringFromBackRef.current = false;
        if (window.withcookieBackState) {
          window.withcookieBackState.isRestoring = false;
        }
      }
    }
  }, [isMobile, loadMobileState]);


  // 모바일 모드 변경 시 초기화
  useEffect(() => {
    if (isMobile && !isRestoringFromBackRef.current) {
      // 저장된 상태가 있는지 확인
      const savedState = loadMobileState();
      if (savedState && savedState.mobileProducts && savedState.mobileProducts.length > 0) {
        setMobileProducts(savedState.mobileProducts);
        setMobilePage(savedState.mobilePage);
      } else {
        // 모바일로 전환 시 첫 페이지 상품만 표시
        setMobileProducts(products);
        setMobilePage(1);
      }
    }
  }, [isMobile, loadMobileState, products]); // products 의존성 추가
  
  // 초기 로드 시 모바일 상품 설정
  useEffect(() => {
    const isRestoring = (typeof window !== 'undefined' && window.withcookieBackState?.isRestoring) || isRestoringFromBackRef.current;
    if (isMobile && products.length > 0 && !isRestoring) {
      // mobileProducts가 비어있거나, products가 변경되었을 때 (필터 변경 등)
      if (mobileProducts.length === 0) {
        setMobileProducts(products);
        setMobilePage(1);
      }
    }
  }, [isMobile, products, mobileProducts.length]);
  
  // 필터 변경 시 모바일 상품 초기화 - 초기 렌더링 시에는 실행하지 않음
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // 뒤로가기 복원 중이면 필터 변경 초기화 방지
    const isRestoring = (typeof window !== 'undefined' && window.withcookieBackState?.isRestoring) || isRestoringFromBackRef.current;
    
    if (isMobile && !isRestoring) {
      // 필터가 변경되면 저장된 상태도 초기화
      clearMobileState();
      setMobileProducts([]);
      setMobilePage(1);
      setCurrentPage(1);
    }
  }, [categoryId, include_product_ids, exclude_product_ids, include_category_ids, exclude_category_ids, isMobile, clearMobileState]);
  
  // 더보기 버튼 클릭 핸들러 - API 직접 호출
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore) return; // 이미 로딩 중이면 중복 실행 방지
    
    setIsLoadingMore(true);
    
    try {
      const nextPage = mobilePage + 1;
      
      // API 직접 호출 옵션 생성
      const apiBaseUrl = window.location.origin;
      const params: any = {
        page: nextPage,
        per_page: itemsPerPage
      };
      
      // 필터 옵션 추가
      if (categoryId) {
        params.category_id = typeof categoryId === 'string' ? parseInt(categoryId, 10) : Number(categoryId);
      }
      
      // ID 배열 변환 함수
      const toIntArray = (value: any): number[] => {
        if (!value) return [];
        if (Array.isArray(value)) {
          return value
            .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
            .filter(id => !isNaN(id));
        }
        const parsed = typeof value === 'string' ? parseInt(value, 10) : Number(value);
        return !isNaN(parsed) ? [parsed] : [];
      };
      
      const includeProductIds = toIntArray(include_product_ids);
      const excludeProductIds = toIntArray(exclude_product_ids);
      const includeCategoryIds = toIntArray(include_category_ids);
      const excludeCategoryIds = toIntArray(exclude_category_ids);
      
      if (includeProductIds.length > 0) params.include_product_ids = includeProductIds;
      if (excludeProductIds.length > 0) params.exclude_product_ids = excludeProductIds;
      if (includeCategoryIds.length > 0) params.include_category_ids = includeCategoryIds;
      if (excludeCategoryIds.length > 0) params.exclude_category_ids = excludeCategoryIds;
      
      // POST 요청으로 변경
      const response = await fetch(`${apiBaseUrl}/api/products/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // 쿠키 포함
        body: JSON.stringify(params)
      });
      
      const data = await response.json();
      
      if (data && data.products && data.products.length > 0) {
        // Redux의 fetchProducts와 동일한 데이터 변환 적용
        const formattedProducts = data.products.map((product: any) => {
          const defaultPrice = product.config?.default_price 
            ? Number(product.config.default_price) 
            : 0;
          const discountedPrice = product.config?.discounted_price 
            ? Number(product.config.discounted_price) 
            : defaultPrice;
          const hasDiscount = discountedPrice < defaultPrice;
          const stockCount = product.config?.stock_count ? Number(product.config.stock_count) : 0;
          
          return {
            id: product.id || 0,
            title: product.title || "상품명",
            description: product.description || "",
            newPrice: discountedPrice,
            oldPrice: defaultPrice,
            companyId: product.companyId,
            config: product.config,
            hasOptions: product.hasOptions || false,
            image: product.config?.img_url || null,
            imageTwo: product.config?.img_url ? product.config?.img_url : null,
            category: product.category_name || "기타",
            rating: 4,
            weight: product.config?.weight || "N/A",
            stockCount: stockCount,
            isInStock: stockCount > 0,
            sale: hasDiscount ? "Sale" : "",
            discountPercent: hasDiscount 
              ? Math.round(100 - (discountedPrice / defaultPrice * 100))
              : 0,
            createdAt: product.createdAt || "",
            updatedAt: product.updatedAt || "",
            tags: product.tags || ""
          };
        });
        
        // 기존 상품에 새로운 상품 추가
        const updatedProducts = [...mobileProducts, ...formattedProducts];
        setMobileProducts(updatedProducts);
        setMobilePage(nextPage);
        
        // 상태 저장
        saveMobileState({
          mobileProducts: updatedProducts,
          mobilePage: nextPage,
          scrollPosition: window.pageYOffset,
          totalPages
        });
      }
    } catch (error) {
      console.error('더보기 로딩 중 오류:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [mobilePage, isLoadingMore, itemsPerPage, categoryId, include_product_ids, exclude_product_ids, include_category_ids, exclude_category_ids, mobileProducts, saveMobileState, totalPages]);
  
  // 더보기 버튼 자동 클릭을 위한 Intersection Observer 설정
  useEffect(() => {
    if (!isMobile || !loadMoreButtonRef.current) return;
    
    const currentButtonRef = loadMoreButtonRef.current; // ref 복사
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 버튼이 화면에 나타났을 때
            if (autoLoadTimerRef.current) {
              clearTimeout(autoLoadTimerRef.current);
            }
            
            // 0.5초 후 자동 클릭 (복원 중이 아닐 때만)
            autoLoadTimerRef.current = setTimeout(() => {
              if (!isLoadingMore && mobilePage < totalPages && loadMoreButtonRef.current && !isRestoringFromBackRef.current) {
                handleLoadMore();
              }
            }, 500);
          } else {
            // 버튼이 화면에서 사라졌을 때 타이머 취소
            if (autoLoadTimerRef.current) {
              clearTimeout(autoLoadTimerRef.current);
              autoLoadTimerRef.current = null;
            }
          }
        });
      },
      {
        threshold: 0.5 // 버튼의 50% 이상이 보일 때 감지
      }
    );
    
    if (currentButtonRef) {
      observer.observe(currentButtonRef);
    }
    
    // 클린업
    return () => {
      if (currentButtonRef) {
        observer.unobserve(currentButtonRef);
      }
      if (autoLoadTimerRef.current) {
        clearTimeout(autoLoadTimerRef.current);
      }
    };
  }, [isMobile, isLoadingMore, mobilePage, totalPages, handleLoadMore]);

  // 스크롤 위치 저장을 위한 이벤트 리스너
  useEffect(() => {
    if (!isMobile || isRestoringFromBackRef.current) return;

    const handleScroll = () => {
      // 스크롤 저장 throttle
      if (scrollSaveTimerRef.current) {
        clearTimeout(scrollSaveTimerRef.current);
      }
      
      scrollSaveTimerRef.current = setTimeout(() => {
        if (mobileProducts.length > 0 && !isRestoringFromBackRef.current) {
          saveMobileState({
            mobileProducts,
            mobilePage,
            scrollPosition: window.pageYOffset,
            totalPages
          });
        }
      }, 1000); // 1초 throttle
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollSaveTimerRef.current) {
        clearTimeout(scrollSaveTimerRef.current);
      }
    };
  }, [isMobile, mobileProducts, mobilePage, totalPages, saveMobileState]);
  
  // 뒤로가기 복원 중 확인
  const isRestoringFromBack = (typeof window !== 'undefined' && window.withcookieBackState?.isRestoring) || isRestoringFromBackRef.current;
  
  // 로딩 중 표시 (뒤로가기 복원 중이면서 모바일 상품이 있으면 로딩 표시 안함)
  if (loading && !(isRestoringFromBack && isMobile && mobileProducts.length > 0)) {
    return (
      <div className="product-loading" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('상품 목록을 불러오는 중입니다...')}</p>
      </div>
    );
  }
  
  // 뒤로가기 복원 중이면서 모바일 상품이 없는 경우에만 별도 로딩 표시
  if (isRestoringFromBack && isMobile && mobileProducts.length === 0) {
    return (
      <div className="product-loading" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('페이지를 복원하는 중입니다...')}</p>
      </div>
    );
  }
  
  // 오류 발생 시 표시
  if (error && !products.length) {
    // 오류 메시지 추출
    let errorMessage = t('알 수 없는 오류가 발생했습니다');
    
    if (isErrorWithMessage(error)) {
      errorMessage = error.message;
    } else if (error) {
      errorMessage = String(error);
    }
    
    return (
      <div className="product-error" style={{padding: "20px", textAlign: "center", color: "red"}}>
        <p>{errorMessage}</p>
      </div>
    );
  }
  
  // 상품이 없는 경우 (뒤로가기 복원 중이거나 모바일 상품이 있으면 제외)
  if (!products.length && !isRestoringFromBack && (!isMobile || mobileProducts.length === 0)) {
    return (
      <div className="no-products" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('상품이 없습니다.')}</p>
      </div>
    );
  }
  
  return (
    <section id={id} className="gi-shop product-list-component" style={{ paddingTop: isMobile ? '20px' : '40px' }}>
      <div className="container" style={{ paddingLeft: isMobile ? '15px' : '15px', paddingRight: isMobile ? '15px' : '15px' }}>
        <Row className={`${className} ${getItemsPerRowClass()}`} style={{ margin: '0' }}>
          <Col lg={lg} md={12} className={`gi-shop-rightside ${order}`} style={{ marginBottom: '30px' }}>
            {/* Shop content Start */}
            <div className={`shop-pro-content ${isGridView ? "list-view-50" : ""}`}>
              <div className="shop-pro-inner">
                <Row>
                  {(isMobile ? mobileProducts : products)
                    .filter((_, index) => !excludeIndexes.includes(index.toString()) && !excludeIndexes.includes(index))
                    .map((product, index) => (
                    <Col 
                      key={index}
                      xl={isGridView ? 12 : xlValue} // 상태로 관리되는 xlValue 사용
                      lg={isGridView ? 12 : 3} // PC에서 항상 4개 표시 (강제 설정)
                      md={isGridView ? 12 : 6} // 태블릿에서 2개 표시
                      sm={isGridView ? 12 : 6} // 작은 태블릿에서 2개 표시
                      xs={isGridView ? 12 : 6} // 모바일에서 2개 표시
                      className={`${isGridView ? "col-md-6 col-xs-6 col-sm-6 width-50" : ""} gi-product-box pro-gl-content`}
                      style={{ paddingLeft: isMobile ? '5px' : '10px', paddingRight: isMobile ? '5px' : '10px', marginBottom: isMobile ? '10px' : '20px' }}
                    >
                      <div className="gi-product-content">
                        <div className={`gi-product-inner ${isGridView ? "d-flex" : ""}`}>
                          <div className={`gi-pro-image-outer ${isGridView ? "flex-shrink-0" : ""}`} 
                            style={isGridView ? {width: '30%'} as ExtendedCSSProperties : productImageStyle.imgContainer}>
                            <div className={`gi-pro-image ${product.image ? "product-hover-area" : ""}`} 
                              style={isGridView ? 
                                {width: "100%", height: "300px", display: "flex", alignItems: "center", justifyContent: "center"} as ExtendedCSSProperties : 
                                productImageStyle.imgWrapper}>
                              <Link 
                                to={`/products/${product.id}`} 
                                className="image" 
                                style={productImageStyle.imageLink}
                                onMouseEnter={product.image ? (e) => handleImageHover(e, true) : undefined}
                                onMouseLeave={product.image ? (e) => handleImageHover(e, false) : undefined}
                                onClick={() => {
                                  if (isMobile && mobileProducts.length > 0) {

                                    // 상태 저장
                                    saveMobileState({
                                      mobileProducts,
                                      mobilePage,
                                      scrollPosition: window.pageYOffset,
                                      totalPages
                                    });
                                    
                                    // 뒤로가기 플래그 설정
                                    sessionStorage.setItem('withcookie_from_back', 'true');
                                  }
                                }}
                              >
                                {/* SafeImage 컴포넌트는 JSX 요소이지만 타입 오류로 인해 as any로 처리 */}
                                {React.createElement(SafeImage as any, {
                                  src: product.image,
                                  alt: product.title,
                                  className: "main-image",
                                  style: product.image ? productImageStyle.mainImage : {},
                                  placeholderStyle: {
                                    position: "absolute" as CSSPosition,
                                    top: 0,
                                    left: 0
                                  }
                                })}
                                
                                {/* 호버 이미지는 imageTwo가 있을 때만 추가 */}
                                {product.image && product.imageTwo && 
                                  React.createElement(SafeImage as any, {
                                    src: product.imageTwo,
                                    alt: product.title,
                                    className: "hover-image",
                                    style: productImageStyle.hoverImage
                                  })
                                }
                              </Link>
                              {product.sale && (
                                <span className="flags">
                                  <span className="sale">{t(product.sale)}</span>
                                </span>
                              )}
                              
                              {/* 상품 액션 버튼 영역 추가 - 호버시 표시 */}
                              {product.image && (
                                <div className="product-action">
                                  <div className="pro-same-action pro-cart">
                                    <button
                                      title={product.hasOptions ? t('옵션 선택') : t('장바구니에 추가')}
                                      onClick={(e) => {
                                        e.preventDefault(); // 이벤트 전파 방지
                                        e.stopPropagation(); // 상위 이벤트 방지
                                        handleAddToCart(product);
                                      }}
                                    >
                                      <i className={product.hasOptions ? "fas fa-plus" : "fas fa-shopping-cart"}></i>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`gi-pro-content ${isGridView ? "flex-grow-1 ps-4" : ""}`}>
                            <h6 className="gi-pro-title">
                              <Link 
                                to={`/products/${product.id}`}
                                onClick={() => {
                                  if (isMobile && mobileProducts.length > 0) {

                                    // 상태 저장
                                    saveMobileState({
                                      mobileProducts,
                                      mobilePage,
                                      scrollPosition: window.pageYOffset,
                                      totalPages
                                    });
                                    
                                    // 뒤로가기 플래그 설정
                                    sessionStorage.setItem('withcookie_from_back', 'true');
                                  }
                                }}
                              >
                                {t(product.title)}
                              </Link>
                            </h6>
                            <div className="gi-pro-price">
                              {(() => {
                                // companyId가 21인 경우 로그인 상태에 따른 가격 표시
                                if (product.companyId === 22) {
                                  const defaultPrice = Number(product.config?.default_price || 0);
                                  const discountedPrice = Number(product.config?.discounted_price || 0);
                                  
                    
                                  
                                  // 더 안전한 로그인 상태 확인
                                  const isLoggedIn = Boolean(isUserLoggedIn);
                                  
                                  if (isLoggedIn) {
                                    // 로그인한 경우 discounted_price 표시
                                    if (defaultPrice > discountedPrice && discountedPrice > 0) {
                                      // 할인이 있는 경우 할인 표시
                                      return React.createElement(DiscountRatePriceDisplay as any, {
                                        discountedPrice: discountedPrice,
                                        originalPrice: defaultPrice,
                                        discountedPriceClassName: "new-price",
                                        originalPriceClassName: "old-price"
                                      });
                                    } else {
                                      // 할인이 없는 경우 일반 가격 표시
                                      return React.createElement(PriceDisplay as any, {
                                        price: discountedPrice || defaultPrice,
                                        className: "new-price"
                                      });
                                    }
                                  } else {
                                    // 로그인하지 않은 경우 default_price 표시
                                    return React.createElement(PriceDisplay as any, {
                                      price: defaultPrice,
                                      className: "new-price"
                                    });
                                  }
                                } else {
                                  // companyId가 21이 아닌 경우 기존 로직 유지
                                  if (product.oldPrice > product.newPrice) {
                                    return React.createElement(DiscountRatePriceDisplay as any, {
                                      discountedPrice: product.newPrice,
                                      originalPrice: product.oldPrice,
                                      discountedPriceClassName: "new-price",
                                      originalPriceClassName: "old-price"
                                    });
                                  } else {
                                    return React.createElement(PriceDisplay as any, {
                                      price: product.newPrice,
                                      className: "new-price"
                                    });
                                  }
                                }
                              })()}
                            </div>
                            {isGridView && (
                              <div className="gi-pro-desc">
                                <p>{product.description || t('상품 설명이 없습니다.')}</p>
                              </div>
                            )}
                            {/* 재고 상태 표시 */}
                            {showStock && (
                              <div className="gi-pro-stock">
                                <span className={`stock-status ${product.stockCount !== undefined && product.stockCount <= 10 && product.stockCount > 0 ? "low-stock" : ""}`}>
                                  {!product.isInStock
                                    ? <span style={{color: "red"}}>{t('품절')}</span> 
                                    : `${t('재고')}: ${product.stockCount || 0}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
              {/* Pagination for PC / Load More for Mobile */}
              <div className="product-pagination">
                {isMobile ? (
                  // 모바일에서는 더보기 버튼
                  <div style={{ 
                    textAlign: 'center', 
                    marginTop: '30px', 
                    marginBottom: '30px',
                    padding: '0 15px'
                  }}>
                    {(mobilePage < totalPages || totalPages === 0) && (
                      <button 
                        ref={loadMoreButtonRef}
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        className="btn btn-primary load-more-btn"
                        style={{
                          padding: '12px 40px',
                          fontSize: '16px',
                          fontWeight: '500',
                          borderRadius: '25px',
                          border: 'none',
                          color: 'white',
                          width: '100%',
                          maxWidth: '300px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {isLoadingMore ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            {t('로딩 중...')}
                          </>
                        ) : (
                          t('더보기')
                        )}
                      </button>
                    )}
                    {mobilePage >= totalPages && totalPages > 0 && (
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        {t('모든 상품을 보셨습니다.')}
                      </div>
                    )}
                  </div>
                ) : (
                  // PC에서는 페이지네이션
                  totalPages > 1 && (
                    <>
                      <div className="pagination-info" style={{ textAlign: 'center', marginBottom: '10px' }}></div>
                      {renderPagination()}
                    </>
                  )
                )}
              </div>
              {/* Pagination End */}
            </div>
            {/* Shop content End */}
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default ProductListComponent; 